import React from 'react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  children: React.ReactNode;
  className?: string;
}

export function Button({
  variant = 'primary',
  size = 'md',
  className = '',
  children,
  ...props
}: ButtonProps) {
  const base = "inline-flex min-h-[44px] items-center justify-center rounded-xl font-semibold transition-all duration-200 active:scale-[0.97] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-950 disabled:opacity-55 disabled:cursor-not-allowed disabled:transform-none";

  const variants = {
    primary: "text-white shadow-md hover:-translate-y-1 hover:shadow-glow relative overflow-hidden",
    secondary: "border border-slate-200/70 bg-white/80 text-slate-800 hover:bg-white hover:border-slate-300 dark:border-slate-700 dark:bg-slate-800/70 dark:text-slate-100 dark:hover:bg-slate-800 shadow-soft hover:shadow-lift hover:-translate-y-0.5",
    ghost: "hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100",
    danger: "bg-accent-danger text-white hover:bg-red-600 shadow-md hover:shadow-lg hover:-translate-y-0.5",
  };

  const sizes = {
    sm: "px-3 py-2 text-sm",
    md: "px-4 py-2.5 text-sm",
    lg: "px-6 py-3.5 text-base",
  };

  return (
    <button
      className={`${base} ${variants[variant]} ${sizes[size]} ${className}`}
      style={variant === 'primary' ? { background: 'linear-gradient(125deg, #2563eb 0%, #0891b2 100%)' } : undefined}
      {...props}
    >
      {variant === 'primary' && (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-xl"
          style={{
            background: 'linear-gradient(105deg, transparent 30%, rgba(255,255,255,0.2) 50%, transparent 70%)',
            backgroundSize: '250% 100%',
            animation: 'shimmerSweep 2.6s linear infinite',
          }}
        />
      )}
      <span className="relative flex items-center justify-center gap-[inherit] w-full">{children}</span>
    </button>
  );
}
